// client/src/pages/EvaluationResultsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, List, Typography, Row, Col, Input, Select, Table, Tag, Space, Button, Modal } from 'antd';
import EvaluationReadOnly from '../components/EvaluationReadOnly';

const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
export default function EvaluationResultsPage() {
  const [years, setYears] = useState([]);
  const [loadingYears, setLoadingYears] = useState(true);

  const [year, setYear] = useState(null);
  const [type, setType] = useState(''); // '', 'Backoffice', 'NewBiz'
  const [q, setQ] = useState('');

  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);

  const [selectedEval, setSelectedEval] = useState(null);
  const [loadingEval, setLoadingEval] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/evaluations/admin/years`, { withCredentials: true });
        setYears(res.data || []);
        if ((res.data || []).length) setYear(res.data[0].year);
      } catch (e) {
        Modal.error({ title: 'Load years failed', content: e?.response?.data?.error || 'Unable to load years' });
      } finally {
        setLoadingYears(false);
      }
    })();
  }, []);

  const loadRows = async () => {
    if (!year) return;
    setLoadingRows(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (type) params.set('type', type);
      const res = await axios.get(`${API_BASE_URL}/api/evaluations/admin/year/${year}?${params.toString()}`, { withCredentials: true });
      setRows(res.data?.data || []);
    } catch (e) {
      Modal.error({ title: 'Load submissions failed', content: e?.response?.data?.error || 'Unable to load submissions' });
      setRows([]);
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [year, type]);

  const viewEval = async (record) => {
    setLoadingEval(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/evaluations/admin/year/${year}/user/${record.userId}`, { withCredentials: true });
      setSelectedEval(res.data);
    } catch (e) {
      Modal.error({ title: 'Load evaluation failed', content: e?.response?.data?.error || 'Unable to load evaluation' });
    } finally {
      setLoadingEval(false);
    }
  };

  const downloadPdf = async (record) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/evaluations/admin/year/${year}/user/${record.userId}/pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const fullName = (record.fullName || record.email || record.userId).replace(/[^\p{L}\p{N}_ -]+/gu, '_');
      downloadBlob(res.data, `evaluation_${year}_${fullName}.pdf`);
    } catch (e) {
      Modal.error({ title: 'Download failed', content: e?.response?.data?.error || 'Unable to download PDF' });
    }
  };

  const columns = [
    { title: 'ชื่อ-นามสกุล', dataIndex: 'fullName', key: 'fullName',
      render: (t, r) => <Button type="link" onClick={()=>viewEval(r)}>{t || '-'}</Button>
    },
    { title: 'อีเมล', dataIndex: 'email', key: 'email' },
    { title: 'ฝ่าย/หน่วยงาน', dataIndex: 'business', key: 'business' },
    { title: 'ตำแหน่ง', dataIndex: 'position', key: 'position' },
    { title: 'ประเภท', dataIndex: 'type', key: 'type',
      render: t => <Tag color={t === 'NewBiz' ? 'volcano' : 'geekblue'}>{t}</Tag>
    },
    { title: 'คะแนนที่ได้', dataIndex: 'totalEarned', key: 'totalEarned' },
    { title: 'ผลลัพธ์', dataIndex: 'grade', key: 'grade',
      render: g => g ? <Tag color="purple">{g}</Tag> : '-'
    },
    { title: 'ส่งเมื่อ', dataIndex: 'submittedAt', key: 'submittedAt',
      render: v => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: 'ไฟล์',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Button onClick={()=>viewEval(r)}>ดูข้อมูล</Button>
          <Button type="primary" onClick={()=>downloadPdf(r)}>ดาวน์โหลด PDF</Button>
        </Space>
      )
    }
  ];

  const filteredRows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter(r => [r.fullName, r.email, r.position, r.business].join(' ').toLowerCase().includes(ql));
  }, [rows, q]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Title level={3} style={{ marginBottom: 0 }}>Evaluation Results</Title>

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <Text strong>Year</Text>
            <Select
              loading={loadingYears}
              style={{ width: '100%' }}
              value={year}
              onChange={(v)=>setYear(v)}
              options={(years || []).map(y => ({ label: `${y.year} (${y.total})`, value: y.year }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Type</Text>
            <Select style={{ width: '100%' }} value={type} onChange={setType}>
              <Option value="">All</Option>
              <Option value="Backoffice">Backoffice</Option>
              <Option value="NewBiz">NewBiz</Option>
            </Select>
          </Col>
          <Col xs={24} md={12}>
            <Text strong>Search</Text>
            <Input.Search
              placeholder="ชื่อ / อีเมล / หน่วยงาน / ตำแหน่ง"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              onSearch={loadRows}
              enterButton
              allowClear
            />
          </Col>
        </Row>
      </Card>

      <Card title={`Submissions — ${year || '-'}`} bodyStyle={{ paddingTop: 0 }}>
        <Table
          rowKey={(r)=>r._id}
          columns={columns}
          dataSource={filteredRows}
          loading={loadingRows}
          pagination={{ pageSize: 15, showSizeChanger: true }}
        />
      </Card>

      {selectedEval && (
        <Card title={`Viewing: ${selectedEval.section1?.fullName || selectedEval.email || selectedEval._id}`} extra={<Button onClick={()=>setSelectedEval(null)}>Close</Button>}>
          <EvaluationReadOnly doc={selectedEval} />
        </Card>
      )}
    </div>
  );
}
